/**
 * enginePreProcessJson.ts - PreProcessJson template engine
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { MergeResult } from '../../../../Assembler/dist/model/mergeResult.js';
import type { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import { mergeWithMetadata as mergeWithMetadataImpl, mergeTemplates, prepareProtectedMergeContext, initializeDynamicMergeState, collectDynamicHandlerResults, createProtectedMergeContext, createProtectedMergeConfig } from './enginePreProcessJson_Merge.js';
import { helperMethods } from './enginePreProcessJson_Helper.js';
import { mergeMetadataMethods } from './enginePreProcessJson_Merge_Metadata.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { IEngine } from '../../../../Assembler/dist/base/engine/iEngine.js';
import type { IEngineTimings } from '../../../../Assembler/dist/base/engine/iEngineTimings.js';
import { EngineTimingData } from '../../../../Assembler/dist/model/engineTimingData.js';
import type { ILoaderPreProcessJson } from '../loader/iLoaderPreProcessJson.js';
import type { TemplateInfoNode, AppSiteMetadataRef } from './enginePreProcessJson_Helper.js';



type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

export type { ILoaderPreProcessJson };

/**
 * PreProcess JSON engine that uses metadata-based merging
 * Equivalent to C# EnginePreProcessJson
 */
export class EnginePreProcessJson extends IEngine implements IEngineTimings {
    // Loader provided at construction time - ensures type safety
    _loader: ILoaderPreProcessJson | null = null;

    // Timing instrumentation (instance-scoped) - all times in nanoseconds
    _getTemplateNanos: number = 0;
    _jsonMergeNanos: number = 0;
    _slotProcessNanos: number = 0;
    _placeholderProcessNanos: number = 0;
    _dynamicDataNanos: number = 0;
    _dynamicViewNanos: number = 0;
    _dynamicActionNanos: number = 0;
    _dynamicClientDataNanos: number = 0;
    _getTemplateCount: number = 0;
    _jsonMergeCount: number = 0;
    _slotProcessCount: number = 0;
    _placeholderProcessCount: number = 0;
    _dynamicDataCount: number = 0;
    _dynamicViewCount: number = 0;
    _dynamicActionCount: number = 0;
    _dynamicClientDataCount: number = 0;

    // Merge-pass properties
    genMergeMissingInfo: boolean = false;
    replaceMissingInfo: string = '';
    maxArrayOperationItems: number = 1;

    /**
     * Convert milliseconds to nanoseconds
     */
    static msToNanos(ms: number): number {
        const nanos = ms * 1_000_000;
        if (!Number.isFinite(nanos)) {
            return 0;
        }
        return Math.round(nanos);
    }

    resetTimings(): void {
        this._getTemplateNanos = 0;
        this._jsonMergeNanos = 0;
        this._slotProcessNanos = 0;
        this._placeholderProcessNanos = 0;
        this._dynamicDataNanos = 0;
        this._dynamicViewNanos = 0;
        this._dynamicActionNanos = 0;
        this._getTemplateCount = 0;
        this._jsonMergeCount = 0;
        this._slotProcessCount = 0;
        this._placeholderProcessCount = 0;
        this._dynamicClientDataNanos = 0;
        this._dynamicDataCount = 0;
        this._dynamicViewCount = 0;
        this._dynamicActionCount = 0;
        this._dynamicClientDataCount = 0;
    }

    getTimingReport(): string {
        // Convert nanos to ms for display
        return `[PreProcessJson Timing] GetTemplate: ${(this._getTemplateNanos / 1_000_000.0).toFixed(2)}ms (${this._getTemplateCount} calls), ` +
            `JsonMerge: ${(this._jsonMergeNanos / 1_000_000.0).toFixed(2)}ms (${this._jsonMergeCount} calls), ` +
            `SlotProcess: ${(this._slotProcessNanos / 1_000_000.0).toFixed(2)}ms (${this._slotProcessCount} calls), ` +
            `PlaceholderProcess: ${(this._placeholderProcessNanos / 1_000_000.0).toFixed(2)}ms (${this._placeholderProcessCount} calls), ` +
            `DynamicData: ${(this._dynamicDataNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicDataCount} calls), ` +
            `DynamicView: ${(this._dynamicViewNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicViewCount} calls), ` +
            `DynamicAction: ${(this._dynamicActionNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicActionCount} calls), ` +
            `DynamicClientData: ${(this._dynamicClientDataNanos / 1_000_000.0).toFixed(2)}ms (${this._dynamicClientDataCount} calls)`;
    }

    getTimingData(): EngineTimingData {
        // Values are stored in nanoseconds
        const timingData = new EngineTimingData();
        timingData.getTemplateNanos = this._getTemplateNanos;
        timingData.getTemplateCount = this._getTemplateCount;
        timingData.jsonMergeNanos = this._jsonMergeNanos;
        timingData.jsonMergeCount = this._jsonMergeCount;
        timingData.slotProcessNanos = this._slotProcessNanos;
        timingData.slotProcessCount = this._slotProcessCount;
        timingData.placeholderProcessNanos = this._placeholderProcessNanos;
        timingData.placeholderProcessCount = this._placeholderProcessCount;
        timingData.dynamicDataNanos = this._dynamicDataNanos;
        timingData.dynamicDataCount = this._dynamicDataCount;
        timingData.dynamicViewNanos = this._dynamicViewNanos;
        timingData.dynamicViewCount = this._dynamicViewCount;
        timingData.dynamicActionNanos = this._dynamicActionNanos;
        timingData.dynamicActionCount = this._dynamicActionCount;
        timingData.dynamicClientDataNanos = this._dynamicClientDataNanos;
        timingData.dynamicClientDataCount = this._dynamicClientDataCount;
        return timingData;
    }

    accumulateDynamicDataTiming(nanos: number, count: number): void {
        this._dynamicDataNanos += nanos;
        this._dynamicDataCount += count;
    }

    accumulateDynamicViewTiming(nanos: number, count: number): void {
        this._dynamicViewNanos += nanos;
        this._dynamicViewCount += count;
    }

    accumulateDynamicClientDataTiming(nanos: number, count: number): void {
        this._dynamicClientDataNanos += nanos;
        this._dynamicClientDataCount += count;
    }

    accumulateDynamicActionTiming(nanos: number, count: number): void {
        this._dynamicActionNanos += nanos;
        this._dynamicActionCount += count;
    }

    ticksToNanos(nanos: number): number {
        return nanos;
    }

    addSlotProcessTiming(nanos: number): void {
        this._slotProcessNanos += nanos;
        this._slotProcessCount++;
    }

    addPlaceholderProcessTiming(nanos: number): void {
        this._placeholderProcessNanos += nanos;
        this._placeholderProcessCount++;
    }

    addJsonMergeTiming(nanos: number): void {
        this._jsonMergeNanos += nanos;
        this._jsonMergeCount++;
    }

    addGetTemplateTiming(nanos: number): void {
        this._getTemplateNanos += nanos;
        this._getTemplateCount++;
    }

    /**
     * Creates a new EnginePreProcessJson instance with the specified loader.
     * The loader is stored and used for all subsequent operations, ensuring type safety.
     */
    constructor(loader: ILoaderPreProcessJson) {
        super();
        if (!loader) {
            throw new Error('loader is required');
        }
        this._loader = loader;
        // Configuration properties
        this.genMergeMissingInfo = false;
        this.replaceMissingInfo = '';
        this.maxArrayOperationItems = 1;
    }

    /**
     * Merges templates using provided metadata
     */
    _mergeWithMetadata(
        appSite: string,
        appFile: string,
        metadata: AppSiteMetadataRef,
        componentHtml: JsonObject,
        genOperationJson: boolean,
        genComponentHtml: boolean,
        contextJson: JsonObject | null = null,
        configJson: JsonObject | null = null,
        clientMode: boolean = false
    ): { html: string; operations: import('../../../../Assembler/dist/model/operationsMetadata.js').OperationsMetadata | null } {
        const loader = this._loader!;
        return mergeWithMetadataImpl(
            this,
            loader,
            appSite,
            appFile,
            metadata,
            componentHtml,
            genOperationJson,
            genComponentHtml,
            contextJson,
            configJson,
            clientMode
        );
    }

    mergeToBytes(appSite: string, appView: string | null, contextJson: JsonObject | null): Uint8Array {
        const mergedHtml = this.mergeToString(appSite, appView, contextJson);
        return new TextEncoder().encode(mergedHtml);
    }

    mergeToString(appSite: string, appView: string | null, contextJson: JsonObject | null): string {
        const result = this.mergeTemplates(appSite, appView, false, false, contextJson ?? new JsonObject());
        return result.componentHtml.get(result.appFile).asString() || '';
    }

    // Method implemented via Object.assign from enginePreProcessJson_Merge.ts
    declare mergeTemplates: (
        appSite: string,
        appView: string | null,
        genOperationJson: boolean,
        genComponentHtml: boolean,
        contextJson: JsonObject | null,
        configJson?: JsonObject | null,
        actionMode?: boolean,
        clientMode?: boolean
    ) => MergeResult;
    declare findTemplateInHierarchy: (root: TemplateInfoNode | null, templateName: string) => TemplateInfoNode | null;
    declare containsNumberPattern: (placeholderName: string) => boolean;
    declare evaluateCondition: (conditionalKey: string, data: JsonObject | PlainJsonMap | PlainJsonObject | null) => boolean;
    declare createFlowStack: (appFile: string) => string[];
    declare buildOperationsMetadata: (genOperationJson: boolean, appSite: string, appFile: string, appView: string | null, templateKey: string, operations: import('../../../../Assembler/dist/model/templateOperation.js').TemplateOperation[], inheritanceOperations: import('../../../../Assembler/dist/model/inheritanceOperation.js').InheritanceOperation[]) => import('./enginePreProcessJson_Helper.js').OperationsMetadata | null;
    declare accumulateDynamicDataTimingFromLoader: () => void;
    declare accumulateDynamicViewTimingFromLoader: () => void;
    declare accumulateDynamicActionTimingFromLoader: () => void;
    declare accumulateDynamicClientDataTimingFromLoader: () => void;
    declare tryReturnStaticHtml: (htmlJson: JsonObject) => { success: boolean; staticHtml: string };
    declare createProtectedMergeContext: (appSite: string, appView: string | null, appFile: string, engineType: string, contextJson: JsonObject) => JsonObject;
    declare createProtectedMergeConfig: (configJson: JsonObject | null) => JsonObject | null;
    declare prepareProtectedMergeContext: (appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null) => { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null };
    declare initializeDynamicMergeState: (appSite: string, appFile: string, appView: string | null, actionMode: boolean) => DynamicResultsJson;
    declare collectDynamicHandlerResults: (dynamicResultsJson: DynamicResultsJson) => { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject };
}

Object.assign(EnginePreProcessJson.prototype, {
    ...helperMethods,
    ...mergeMetadataMethods,
    mergeTemplates,
    prepareProtectedMergeContext,
    initializeDynamicMergeState,
    collectDynamicHandlerResults,
    createProtectedMergeContext,
    createProtectedMergeConfig
});
